import React, { useMemo, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { MdCheck } from 'react-icons/md'
import { FiDownload } from 'react-icons/fi'
import styles from './styles.module.scss'
import { saveGrade, saveLessonFact, setData } from '../../../../actions/journal'
import saveExcel from '../../../../utils/saveExcel'
import moment from 'moment'
import { usePermission } from '../../../../hooks'

const key3 = (sheid, weekId, mid, gradeType) => `${sheid}_${weekId}_${mid}_${gradeType}`
const keyLesson = (sheid, weekId, gradeType) => `${sheid}_${weekId}_${gradeType}`

const formatAvg = (avg) => (typeof avg === 'number' ? avg.toFixed(2).replace('.', ',') : '')

// Редактируемая ячейка оценки/отсутствия.
const GradeCell = ({ value, rk, highlighted, onMouseEnter, onMouseLeave, onSave, canUpdate }) => {
  const [val, setVal] = useState(value || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => { setVal(value || '') }, [value])

  const commit = async () => {
    const next = val.trim()
    if (next === (value || '')) return
    setSaving(true)
    const ok = await onSave(next)
    setSaving(false)
    if (!ok) setVal(value || '') // при ошибке возвращаем прежнее значение
  }

  return (
    <div
      className={`${styles.cell} ${rk ? styles.rkCell : ''} ${highlighted ? styles.rowHover : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <input
        className={styles.gradeInput}
        value={val}
        maxLength={4}
        disabled={!canUpdate || saving}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
      />
    </div>
  )
}

// Чекбокс факта проведения занятия (блокируется на время запроса).
const FactCheckbox = ({ checked, span, onToggle }) => {
  const [saving, setSaving] = useState(false)

  const toggle = async () => {
    if (saving) return
    setSaving(true)
    await onToggle(!checked)
    setSaving(false)
  }

  return (
    <div className={`${styles.cell} ${styles.sumCell}`} style={{ gridColumn: `span ${span}` }}>
      <button
        type="button"
        className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ''}`}
        disabled={saving}
        onClick={toggle}
      >
        {checked && <MdCheck />}
      </button>
    </div>
  )
}

const Table = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const data = useSelector(state => state.journal.data)
  const loading = useSelector(state => state.journal.loading)
  const teacherMid = useSelector(state => state.auth.mid)
  const { readRight, updateRight } = usePermission('Режимы', 'Журнал учета учебных занятий')

  const lessons = (data && data.lessons) || []
  const students = (data && data.students) || []

  // mid ученика, на строке которого сейчас курсор (для подсветки строки).
  const [hoveredMid, setHoveredMid] = useState(null)

  // Плоский список подстолбцов: для занятия с РК — два (обычный + РК), иначе один.
  const subColumns = useMemo(() => {
    const cols = []
    lessons.forEach((lesson) => {
      cols.push({ lesson, gradeType: 0 })
      if (lesson.iscontrol) cols.push({ lesson, gradeType: 1 })
    })
    return cols
  }, [lessons])

  const gradeMap = useMemo(() => {
    const map = {}
    if (data) {
      (data.grades || []).forEach(g => { map[key3(g.sheid, g.week_id, g.student_mid, 0)] = g.grade })
      ;(data.rk_grades || []).forEach(g => { map[key3(g.sheid, g.week_id, g.student_mid, 1)] = g.grade })
    }
    return map
  }, [data])

  const lessonTotalMap = useMemo(() => {
    const map = {}
    if (data) (data.totals_per_lesson || []).forEach(t => { map[keyLesson(t.sheid, t.week_id, t.grade_type)] = t })
    return map
  }, [data])

  const studentAvgMap = useMemo(() => {
    const map = {}
    if (data) (data.totals_per_student || []).forEach(t => { map[t.student_mid] = t.avg })
    return map
  }, [data])

  // ФИО — около четверти ширины, «Средний балл» — узкий фиксированный,
  // остаток поровну делят подстолбцы занятий (не уже 60px — дальше прокрутка).
  const gridTemplate = `minmax(300px, 25%) repeat(${subColumns.length}, minmax(60px, 1fr)) 75px`

  // Сохранение оценки/отсутствия с точечным обновлением итогов.
  const handleSaveGrade = async (lesson, student, gradeType, grade) => {
    const result = await dispatch(saveGrade({
      sheid: lesson.sheid,
      week_id: lesson.week_id,
      student_mid: student.mid,
      grade,
      grade_type: gradeType,
      activity: 0,
      comment: '',
      teacher_mid: teacherMid,
    }))

    if (!result || !result.success) return false

    const listKey = gradeType === 1 ? 'rk_grades' : 'grades'
    const filtered = (data[listKey] || []).filter(
      g => !(g.sheid === lesson.sheid && g.week_id === lesson.week_id && g.student_mid === student.mid),
    )
    const nextList = grade
      ? [...filtered, { sheid: lesson.sheid, week_id: lesson.week_id, student_mid: student.mid, grade, grade_type: gradeType }]
      : filtered

    const lt = result.lesson_total
    const nextTotals = lt
      ? [
        ...(data.totals_per_lesson || []).filter(
          t => !(t.sheid === lt.sheid && t.week_id === lt.week_id && t.grade_type === lt.grade_type),
        ),
        lt,
      ]
      : data.totals_per_lesson

    const sa = result.student_avg
    const nextStudentTotals = sa
      ? [
        ...(data.totals_per_student || []).filter(t => t.student_mid !== sa.student_mid),
        sa,
      ]
      : data.totals_per_student

    dispatch(setData({
      ...data,
      [listKey]: nextList,
      totals_per_lesson: nextTotals,
      totals_per_student: nextStudentTotals,
    }))
    return true
  }

  // Установка/снятие факта проведения занятия.
  const handleToggleFact = async (lesson, isFact) => {
    const result = await dispatch(saveLessonFact({
      sheid: lesson.sheid,
      week_id: lesson.week_id,
      is_fact: isFact,
    }))

    if (!result || !result.success) return false

    dispatch(setData({
      ...data,
      lessons: data.lessons.map(l => (
        l.sheid === lesson.sheid && l.week_id === lesson.week_id
          ? { ...l, is_fact: result.is_fact }
          : l
      )),
    }))
    return true
  }

  // Скачать журнал в Excel.
  const excelExport = () => {
    const header = [
      'ФИО',
      ...subColumns.map(({ lesson, gradeType }) => (
        `${lesson.lesson_date}${gradeType === 1 ? ' (РК)' : ''} ${lesson.alias || ''}`.trim()
      )),
      'Средний балл',
    ]
    const studentRows = students.map(student => ([
      student.fio,
      ...subColumns.map(({ lesson, gradeType }) => gradeMap[key3(lesson.sheid, lesson.week_id, student.mid, gradeType)] || ''),
      formatAvg(studentAvgMap[student.mid]),
    ]))
    const avgRow = ['Средний балл', ...subColumns.map(({ lesson, gradeType }) => (
      formatAvg((lessonTotalMap[keyLesson(lesson.sheid, lesson.week_id, gradeType)] || {}).avg)
    )), '']
    const factRow = ['Занятие проведено', ...subColumns.map(({ lesson, gradeType }) => (
      gradeType === 0 ? (lesson.is_fact ? '+' : '') : ''
    )), '']
    const absentRow = ['Кол-во отсутствующих', ...subColumns.map(({ lesson, gradeType }) => {
      const total = lessonTotalMap[keyLesson(lesson.sheid, lesson.week_id, gradeType)]
      return total ? total.absent_count : 0
    }), '']
    const countRow = ['Кол-во оценок', ...subColumns.map(({ lesson, gradeType }) => {
      const total = lessonTotalMap[keyLesson(lesson.sheid, lesson.week_id, gradeType)]
      return total ? total.grades_count : 0
    }), '']

    saveExcel([header, ...studentRows, avgRow, factRow, absentRow, countRow], 'Журнал учета учебных занятий')
  }

  // Перейти к расходу личного состава (как в старом журнале).
  const goToRashod = () => {
    const first = lessons[0]
    const lessonfulldate = first ? moment(first.lesson_date, 'DD.MM.YYYY').toISOString() : undefined
    history.push('/personnel', { forPersonnel: { lessonfulldate } })
  }

  if (loading) {
    return <div className={styles.state}>Загрузка журнала…</div>
  }
  if (!data) {
    return (
      <div className={styles.state}>
        Выберите учебный период, группу и дисциплину и нажмите «Обновить».
      </div>
    )
  }
  if (!lessons.length || !students.length) {
    return <div className={styles.state}>По выбранным параметрам нет данных журнала.</div>
  }

  return (
    <>
      <div className={styles.tableScroll}>
        <div className={styles.table} style={{ gridTemplateColumns: gridTemplate }}>
          {/* header */}
          <div className={`${styles.headCell} ${styles.headName}`}>ФИО</div>
          {lessons.map((lesson) => {
            console.log(lesson.lesson_date)
            const date = moment(lesson.lesson_date, 'DD.MM.YYYY').format('DD.MM')
            return (
              <Link
                key={`h-${lesson.sheid}-${lesson.week_id}`}
                to={`/journal/${lesson.sheid}`}
                className={styles.headCell}
                style={{ gridColumn: `span ${lesson.iscontrol ? 2 : 1}` }}
                title={lesson.typename || ''}
              >
                <span className={styles.headDate}>{date}</span>
                <span className={styles.typeBadge}>{lesson.alias || ''}</span>
                {lesson.iscontrol && <span className={styles.rkHint}>оц · РК</span>}
              </Link>
            )
          })}
          <div className={`${styles.headCell} ${styles.headPlain} ${styles.avgCell}`}>Средний балл</div>

          {/* students */}
          {students.map((student) => {
            const highlighted = hoveredMid === student.mid
            const onEnter = () => setHoveredMid(student.mid)
            const onLeave = () => setHoveredMid(null)
            return (
              <React.Fragment key={`s-${student.mid}`}>
                <div
                  className={`${styles.cell} ${styles.nameCell} ${highlighted ? styles.rowHover : ''}`}
                  title={student.title || ''}
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}
                >
                  {student.fio}
                </div>
                {subColumns.map(({ lesson, gradeType }) => (
                  <GradeCell
                    key={`g-${student.mid}-${lesson.sheid}-${lesson.week_id}-${gradeType}`}
                    value={gradeMap[key3(lesson.sheid, lesson.week_id, student.mid, gradeType)]}
                    rk={gradeType === 1}
                    highlighted={highlighted}
                    onMouseEnter={onEnter}
                    onMouseLeave={onLeave}
                    canUpdate={updateRight}
                    onSave={grade => handleSaveGrade(lesson, student, gradeType, grade)}
                  />
                ))}
                <div
                  className={`${styles.cell} ${styles.avgCell} ${highlighted ? styles.rowHover : ''}`}
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}
                >
                  {formatAvg(studentAvgMap[student.mid])}
                </div>
              </React.Fragment>
            )
          })}

          {/* средний балл по столбцу */}
          <div className={`${styles.cell} ${styles.sumCell} ${styles.nameCell}`}>Средний балл</div>
          {subColumns.map(({ lesson, gradeType }) => (
            <div
              key={`colavg-${lesson.sheid}-${lesson.week_id}-${gradeType}`}
              className={`${styles.cell} ${styles.sumCell} ${gradeType === 1 ? styles.rkCell : ''}`}
            >
              {formatAvg((lessonTotalMap[keyLesson(lesson.sheid, lesson.week_id, gradeType)] || {}).avg)}
            </div>
          ))}
          <div className={`${styles.cell} ${styles.sumCell} ${styles.avgCell}`} />

          {/* занятие проведено */}
          <div className={`${styles.cell} ${styles.sumCell} ${styles.nameCell}`}>Занятие проведено</div>
          {lessons.map((lesson) => (
            <FactCheckbox
              key={`fact-${lesson.sheid}-${lesson.week_id}`}
              checked={Boolean(lesson.is_fact)}
              span={lesson.iscontrol ? 2 : 1}
              onToggle={isFact => handleToggleFact(lesson, isFact)}
            />
          ))}
          <div className={`${styles.cell} ${styles.sumCell} ${styles.avgCell}`} />

          {/* кол-во отсутствующих */}
          <div className={`${styles.cell} ${styles.sumCell} ${styles.nameCell}`}>Кол-во отсутствующих</div>
          {subColumns.map(({ lesson, gradeType }) => {
            const total = lessonTotalMap[keyLesson(lesson.sheid, lesson.week_id, gradeType)]
            return (
              <div
                key={`abs-${lesson.sheid}-${lesson.week_id}-${gradeType}`}
                className={`${styles.cell} ${styles.sumCell} ${gradeType === 1 ? styles.rkCell : ''}`}
              >
                {total ? total.absent_count : 0}
              </div>
            )
          })}
          <div className={`${styles.cell} ${styles.sumCell} ${styles.avgCell}`} />

          {/* кол-во оценок */}
          <div className={`${styles.cell} ${styles.sumCell} ${styles.nameCell}`}>Кол-во оценок</div>
          {subColumns.map(({ lesson, gradeType }) => {
            const total = lessonTotalMap[keyLesson(lesson.sheid, lesson.week_id, gradeType)]
            return (
              <div
                key={`cnt-${lesson.sheid}-${lesson.week_id}-${gradeType}`}
                className={`${styles.cell} ${styles.sumCell} ${gradeType === 1 ? styles.rkCell : ''}`}
              >
                {total ? total.grades_count : 0}
              </div>
            )
          })}
          <div className={`${styles.cell} ${styles.sumCell} ${styles.avgCell}`} />
        </div>
      </div>

      <div className={styles.actions}>
        <button type="button" className={styles.actionButton} onClick={goToRashod}>
          Расход личного состава
        </button>
        <button type="button" className={styles.actionButton} onClick={excelExport}>
          Скачать эл. таблицу
          <FiDownload size={24} />
        </button>
      </div>
    </>
  )
}

export default Table
